// src/com/auto_plan_bind_group/AutoPlanBindGroupInput.js
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { Button, DialogContent, DialogActions, Grid, CircularProgress, IconButton } from '@mui/material';
import HomeWorkIcon from '@mui/icons-material/HomeWork'; // Icon for Logistics Center
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown'; // Icon for direction
import StoreIcon from '@mui/icons-material/Store'; // Icon for Store
import WrongLocationIcon from '@mui/icons-material/WrongLocation'; // remove store
import AddLocationIcon from '@mui/icons-material/AddLocation'; // add store
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'; // move up
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'; // move down
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'; // Duplication found
import { ContentConainer, ContentHeader } from '../../common/TopViewControl';
import { AppWord, AppObject, AppStyle } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import VzmIntField from '../../common/VzmIntField';
import VzmSelector from '../../common/VzmSelector';
import { ErrorHintBox } from '../../common/VzmStyled';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import InputUtil from '../../value/InputUtil';
import { AutoPlanBindGroupDictionary } from './AutoPlanBindGroupDict';
import { useAlertSetter } from '../../api/app-hook';
import {
    arriveInfoListForRouteSharingRepo, autoPlanBindGroupListRepo, userInfoRepo, vehTypeGroupOrderAllListRepo
} from '../../value/AppModel';
import { LIST_ARRIVE_INFO_FOR_ROUTE_SHARING } from '../arrive_info/ArriveInfoGql';
import { LIST_VEH_TYPE_GROUP_ORDER_ALL } from '../veh_type_group_order/VehTypeGroupOrderGql';
import { ArriveInfoDictionary } from '../arrive_info/ArriveInfoDict';
import FlexyTable from '../../common/flexytable/FlexyTable';
import SearchBox from '../../common/SearchBox';
import {
    getAutoPlanBindGroupMap, AutoPlanBindDupStatus, checkAutoPlanBindGroupDuplication
} from '../auto_plan_bind_store_set/AutoPlanBindStoreSetGql';

const MainContainer = styled(Box)({
    display: 'flex', minHeight: '50vh'
});

// Left
const EditBox = styled(Box)({
    width: 300, border: AppStyle.BorderCCC, borderRadius: 5, padding: '15px 10px 5px 10px',
    overflow: 'auto', 
});

const PlaceBox = styled(Box)({
    padding: 5, borderRadius: 5, display: 'flex', alignItems: 'center',
    '&:hover': {backgroundColor: '#eee'}
});

const StoreIdBox = styled(Box)({minWidth: 50, textAlign: 'center', fontSize: '0.7rem' });

const PlaceName = styled(Box)({flexGrow: 1, textAlign: 'center',});

const ArrowBox = styled(Box)({display:'flex', justifyContent:'center', alignItems:'center'});

// Right
const StoreListBox = styled(Box)({
    flexGrow: 1, minWidth: 600,
});

const InputMap = AutoPlanBindGroupDictionary.inputMap;
const defaultInputData = InputUtil.defaultValuesFromInputMap(InputMap);
const StoreTableProps = ArriveInfoDictionary.storesSharingRouteTableProps;

/**
 * (AUTO_PLAN_BIND_GROUP) 정보 생성 또는 편집 Dialog
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-10-15 15:23:01 using DbdbTap
 */
export default function AutoPlanBindGroupInput({
    open,
    item,
    onClose,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const [storesToShare, setStoresToShare] = useState([]);
    const autoPlanBindGroupListData = useReactiveVar(autoPlanBindGroupListRepo);
    const [autoPlanBindDupStatus, setAutoPlanBindDupStatus] = useState(AutoPlanBindDupStatus.NONE);
    const vehTypeGroupOrderAllLisData = useReactiveVar(vehTypeGroupOrderAllListRepo);
    const [vehTypesOfDc, setVehTypesOfDc] = useState([]);
    const storeListData = useReactiveVar(arriveInfoListForRouteSharingRepo);
    const [searchText, setSearchText] = useState('');
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.
    const autoPlanBindGroupMap = useRef({});

    const [getArriveInfoListForRouteSharing, responseList] = useLazyQuery(LIST_ARRIVE_INFO_FOR_ROUTE_SHARING, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetArriveInfoList(data, option)},
		onError: (error) => addAlert({title: '점포목록조회 오류', message: [error.message]})
    });

    const [getVehTypeGroupOrderAllList, responseVehTypeGroupOrderAllList] = useLazyQuery(LIST_VEH_TYPE_GROUP_ORDER_ALL, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetVehTypeGroupOrderAllList(data, option)},
        onError: (error) => addAlert({title: '차종목록조회 오류', message: [error.message]})
    });

    // const dcTypeCdOptions = ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.DC_TYPE_CD, InputMap.dcTypeCd.id);

    useEffect(()=>{
        if(open) {
            const inputSet = {...AppObject.emptyInputData};
            let editing = false;
            if(item) {
                inputSet.data = {...item};
                editing = true;
                const stores = [...item.storeSetList];
                setStoresToShare(stores);
                inputSet.data.storeSetList = stores.map((s)=>{return {storeId:s.storeId, setOrderNum:s.setOrderNum}});
            }
            else {
                inputSet.data = {...defaultInputData};
                inputSet.data.storeSetList = [];
                inputSet.error.storeSetList = true;
                inputSet.hasError = true;
                setStoresToShare([]);
            }
            setInput(inputSet);
            setIsEdit(editing);
            setSearchText('');
            setAutoPlanBindDupStatus(AutoPlanBindDupStatus.NotExist);
        }
    }, [open, item]);

    useEffect(()=>{
        if(open) {
            const diff = new Date().getTime()/1000 - storeListData.when;
            if(diff > 600) {
                getArriveInfoListForRouteSharing();
            }
        }
    }, [open, storeListData]);

    useEffect(()=>{
        if(open) {
            const diff = new Date().getTime()/1000 - vehTypeGroupOrderAllLisData.when;
            if(diff > 600) {
                getVehTypeGroupOrderAllList();
            }
            else {
                const dcCd = sessionInfo.user.getDcCd();
                const vehTypes = vehTypeGroupOrderAllLisData.records.filter((item)=>item.dcCd === dcCd);
                setVehTypesOfDc(vehTypes);
            }
        }
    }, [open, vehTypeGroupOrderAllLisData]);

    useEffect(()=>{
        autoPlanBindGroupMap.current = getAutoPlanBindGroupMap(autoPlanBindGroupListData.records);
    }, [autoPlanBindGroupListData]);

    if(!open) return null;

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetArriveInfoList = (data, option) => {
        if(data.arriveInfoListForRouteSharing) {
            arriveInfoListForRouteSharingRepo({
                when: new Date().getTime()/1000,
                records: data.arriveInfoListForRouteSharing
            });
        }
    };

    const onCompleteGetVehTypeGroupOrderAllList = (data, option) => {
        if(data.vehTypeGroupOrderListAll) {
            vehTypeGroupOrderAllListRepo({
                when: new Date().getTime()/1000,
                records: data.vehTypeGroupOrderListAll
            });
        }
    };

    // >>>>>>>>> Events <<<<<<<<<<<<<
    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        setInput(newInput);
        // Check duplicated sequence with same veh type.
        if(
            (id===InputMap.condiVehTypeId.id || id===InputMap.storeSetList.id)
            && !newInput.error.condiVehTypeId && newInput.data.storeSetList.length > 1
            && newInput.data.condiVehTypeId
        ) {
            setAutoPlanBindDupStatus(
                checkAutoPlanBindGroupDuplication(
                    autoPlanBindGroupMap.current,
                    isEdit ? newInput.data.groupOrderNum : undefined,
                    newInput.data.condiVehTypeId, newInput.data.storeSetList));
        }
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        onRequestSave(param, isEdit);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    // 점포 검색.
    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    // storesToShare에 있는 것들은 우측 테이블에 나타나선 안된다.
    const filterStoreList = (item) => {
        for(const store of storesToShare) {
            if(store.storeId === item.storeId) return false;
        }
        return true;
    };

    const onClickRemoveStore = (store) => {
        const newStores = storesToShare.filter((s)=>s.storeId !== store.storeId);
        setStoresToShare(newStores);
        const storesInData = newStores.map((store, idx) => {
            return {
                storeId: store.storeId, // storeName: store.storeName,
                setOrderNum: idx+1};
        });
        onChangeValue(InputMap.storeSetList.id, storesInData, storesInData.length < 2);
    };

    const onClickMoveUp = (store, currentIndex) => {
        if(currentIndex < 1) return;
        const newStores = [...storesToShare];
        const temp = newStores[currentIndex-1];
        newStores[currentIndex-1] = store;
        newStores[currentIndex] = temp;
        setStoresToShare(newStores);
        const storesInData = newStores.map((store, idx) => {
            return {
                storeId: store.storeId, // storeName: store.storeName,
                setOrderNum: idx+1};
        });
        onChangeValue(InputMap.storeSetList.id, storesInData, storesInData.length < 2);
    };

    const onClickMoveDown = (store, currentIndex) => {
        if(currentIndex >= storesToShare.length-1) return;
        const newStores = [...storesToShare];
        const temp = newStores[currentIndex+1];
        newStores[currentIndex+1] = store;
        newStores[currentIndex] = temp;
        setStoresToShare(newStores);
        const storesInData = newStores.map((store, idx) => {
            return {
                storeId: store.storeId, // storeName: store.storeName,
                setOrderNum: idx+1};
        });
        onChangeValue(InputMap.storeSetList.id, storesInData, storesInData.length < 2);
    };

    // Add store to storesToShare
    const requestAdd = (row) => {
        const newStores = [...storesToShare];
        newStores.push(row);
        setStoresToShare(newStores);
        const storesInData = newStores.map((store, idx) => {
            return {
                storeId: store.storeId, // storeName: store.storeName,
                setOrderNum: idx+1};
        });
        onChangeValue(InputMap.storeSetList.id, storesInData, storesInData.length < 2);
    };

    const onClickToolOnRowExc = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.NEW:
                requestAdd(row);
                break;
            default: return;
        }
    };

    // tools: add store to storesToShare
    const tools = [
        {id: AppObject.command.NEW, icon: <AddLocationIcon />},
    ]

    // Input field initiallized using getInputField(c,t,i)
    return(
        <DraggableEditor open={open} maxWidth='lg'
            title={isEdit ? '동적점포 설정 수정' : '동적점포 설정 등록'}
        >
            <DialogContent>
                <MainContainer>
                    <EditBox>
                        <Grid container spacing={2}>
                            <Grid item xs={7}>
                                <VzmSelector
                                    dict={InputMap.condiVehTypeId}
                                    value={input.data.condiVehTypeId || ''}
                                    list={vehTypesOfDc}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                    disabled={responseVehTypeGroupOrderAllList.loading}
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <VzmIntField
                                    dict={InputMap.condiPtCnt}
                                    value={input.data.condiPtCnt}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                {
                                    autoPlanBindDupStatus === AutoPlanBindDupStatus.NotExist ? null :
                                    <Box
                                        style={{
                                            color: AppStyle.WarnRGB,
                                            backgroundColor: '#eee',
                                            textAlign: 'center',
                                            fontSize: '0.8rem',
                                            padding: '10px 0'
                                        }}
                                    >
                                        <AutoAwesomeIcon fontSize='8pt'
                                            style={{marginRight: 5, color: 'red'}}
                                        />
                                        {
                                            autoPlanBindDupStatus === AutoPlanBindDupStatus.Same
                                            ?
                                            <span>
                                                동일한 톤수, 동일한 점포 순의<br/>
                                                동적 설정이 이미 있습니다.
                                            </span>
                                            :
                                            <span>
                                                동일한 톤수에 같은 점포로 구성된<br/>
                                                동적 설정이 이미 있습니다. (순서 다름)
                                            </span>
                                        }
                                    </Box>
                                }
                                <PlaceBox>
                                    <HomeWorkIcon fontSize={AppWord.LARGE}
                                        style={{marginRight: 5, color: AppStyle.HomeplusRed}}
                                    />
                                    <PlaceName>
                                    {isEdit ? input.data.dcName : sessionInfo.user.dscNameAs}
                                    </PlaceName>
                                    <Box width={70}>&nbsp;</Box>
                                </PlaceBox>
                                {
                                    storesToShare.map((store, idx) => {
                                        return (
                                            <React.Fragment key={store.storeId}>
                                                <ArrowBox><KeyboardDoubleArrowDownIcon color={AppStyle.mui.disabled} /></ArrowBox>
                                                <PlaceBox>
                                                    <StoreIcon fontSize={AppWord.LARGE}
                                                        style={{marginRight: 5}}
                                                        color={AppStyle.mui.secondary}
                                                    />
                                                    <PlaceName>
                                                        {store.storeName}
                                                        <StoreIdBox>[{store.storeId}]</StoreIdBox>
                                                    </PlaceName>
                                                    {
                                                        idx > 0 &&
                                                        <IconButton
                                                            onClick={()=>onClickMoveUp(store, idx)}
                                                            color={AppStyle.mui.success}
                                                            size={AppWord.SMALL}
                                                        >
                                                            <ArrowUpwardIcon />
                                                        </IconButton>
                                                    }
                                                    {
                                                        idx < storesToShare.length-1 &&
                                                        <IconButton
                                                            onClick={()=>onClickMoveDown(store, idx)}
                                                            color={AppStyle.mui.success}
                                                            size={AppWord.SMALL}
                                                        >
                                                            <ArrowDownwardIcon />
                                                        </IconButton>
                                                    }
                                                    <IconButton
                                                        color={AppStyle.mui.error}
                                                        onClick={()=>onClickRemoveStore(store)}
                                                        size={AppWord.SMALL}
                                                    >
                                                        <WrongLocationIcon />
                                                    </IconButton>
                                                </PlaceBox>
                                            </React.Fragment>
                                        )
                                    })
                                }
                            </Grid>
                        </Grid>
                    </EditBox>
                    <StoreListBox>
                        <ContentConainer>
                            <ContentHeader>
                                <SearchBox
                                    value={searchText}
                                    onChangeText={onChangeSearchText}
                                />
                            </ContentHeader>
                            <FlexyTable
                                name={StoreTableProps.tableId}
                                tableProps={StoreTableProps}
                                records={
                                    storesToShare.length > 0 ?
                                    storeListData.records.filter(filterStoreList) :
                                    storeListData.records
                                }
                                search={searchText}
                                tools={tools}
                                onClickTool={onClickToolOnRowExc}
                            />
                        </ContentConainer>
                    </StoreListBox>
                </MainContainer>
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    input.errorLabels && input.errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {input.errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
